import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, LogOut, RefreshCw } from "lucide-react";
import { SectionLabel } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  nuova: "text-neon border-neon/50",
  confermata: "text-emerald-300 border-emerald-400/40",
  annullata: "text-red-300 border-red-400/40",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("bella_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reservations, setReservations] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/reservations`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error();
      setReservations(await res.json());
    } catch {
      setReservations(null);
      localStorage.removeItem("bella_admin_token");
      setToken("");
      toast.error("Sessione scaduta, accedi di nuovo.");
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore");
      localStorage.setItem("bella_admin_token", data.token);
      setToken(data.token);
      toast.success("Benvenuto!");
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    await fetch(`${API}/reservations/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    load(token);
  };

  const remove = async (id) => {
    await fetch(`${API}/reservations/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    toast.success("Prenotazione eliminata");
    load(token);
  };

  const inputCls =
    "w-full bg-pine border border-border text-foreground px-4 py-3.5 rounded-sm placeholder:text-fog/50 focus:outline-none focus:ring-1 focus:ring-neon focus:border-neon transition-colors duration-300";

  if (!token) {
    return (
      <main className="pt-20 min-h-screen" data-testid="admin-login-page">
        <section className="max-w-md mx-auto px-5 pt-20 pb-24">
          <SectionLabel number="·">Area riservata</SectionLabel>
          <h1 className="font-display uppercase text-5xl text-foreground mb-10">Accesso</h1>
          <form onSubmit={login} className="border border-neon/20 bg-pine/60 p-8 space-y-6" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">Email</label>
              <input id="admin-email" data-testid="admin-input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">Password</label>
              <input id="admin-password" data-testid="admin-input-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="admin-dashboard">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <SectionLabel number="·">Area riservata</SectionLabel>
            <h1 className="font-display uppercase text-5xl sm:text-6xl text-foreground">
              Preno<span className="text-neon">tazioni</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load(token)} data-testid="admin-refresh-btn"
              className="flex items-center gap-2 border border-neon/30 text-fog font-head font-bold text-xs tracking-[0.2em] uppercase px-5 py-3 rounded-full hover:text-neon hover:border-neon transition-colors duration-300">
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button onClick={() => { localStorage.removeItem("bella_admin_token"); setToken(""); }} data-testid="admin-logout-btn"
              className="flex items-center gap-2 border border-neon/30 text-fog font-head font-bold text-xs tracking-[0.2em] uppercase px-5 py-3 rounded-full hover:text-red-300 hover:border-red-400/50 transition-colors duration-300">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        {reservations === null ? (
          <p className="text-fog" data-testid="admin-loading">Caricamento...</p>
        ) : reservations.length === 0 ? (
          <div className="border border-neon/15 bg-pine p-14 text-center" data-testid="admin-empty">
            <p className="font-head uppercase tracking-[0.25em] text-fog">Nessuna prenotazione per ora</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-reservations-list">
            {reservations.map((r) => (
              <div key={r.id} className="border border-neon/15 bg-pine p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`admin-reservation-${r.id.slice(0, 8)}`}>
                <div className="md:col-span-3">
                  <h3 className="font-head font-bold uppercase tracking-wide text-lg text-foreground">{r.name}</h3>
                  <a href={`tel:${r.phone}`} className="text-fog text-sm hover:text-neon transition-colors">{r.phone}</a>
                  {r.email && <p className="text-fog/70 text-sm">{r.email}</p>}
                </div>
                <div className="md:col-span-3 text-fog text-sm">
                  <p><span className="text-foreground font-semibold">{r.date}</span> alle <span className="text-foreground font-semibold">{r.time}</span></p>
                  <p>{r.guests} {r.guests === 1 ? "persona" : "persone"} · {r.seating === "fuori" ? "Fuori" : "Dentro (sala climatizzata)"}</p>
                </div>
                <div className="md:col-span-3 text-fog text-sm">
                  {r.notes ? r.notes : <span className="text-fog/50">Nessuna nota</span>}
                </div>
                <div className="md:col-span-3 flex flex-wrap items-center gap-2 md:justify-end">
                  {["nuova", "confermata", "annullata"].map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} data-testid={`admin-status-${s}-${r.id.slice(0, 8)}`}
                      className={`font-head font-bold text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border transition-colors duration-300 ${
                        r.status === s ? STATUS_STYLES[s] : "text-fog/50 border-foreground/15 hover:text-fog"
                      }`}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove(r.id)} aria-label="Elimina" data-testid={`admin-delete-${r.id.slice(0, 8)}`}
                    className="p-2 text-fog/50 hover:text-red-300 transition-colors duration-300">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
